import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast, Toaster } from "sonner";
import {
    ImagePlus,
    UploadCloud,
    Trash2,
    Plus,
    Loader2,
    X,
    MessageSquarePlus,
    RefreshCw,
} from "lucide-react";
import api, { API, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useUnsavedChanges } from "../contexts/NavigationGuardContext";
import { ConfirmPhotoDelete } from "../components/ConfirmPhotoDelete";

const CR_STATUSES = [
    { key: "submitted", label: "Submitted", dot: "bg-amber-400" },
    { key: "in_progress", label: "In progress", dot: "bg-sky-400" },
    { key: "completed", label: "Completed", dot: "bg-emerald-400" },
];

function relativeTime(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const mins = Math.round(diffMs / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.round(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
}

function StatusPill({ status }) {
    const s = CR_STATUSES.find((x) => x.key === status) || CR_STATUSES[0];
    return (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-zinc-800 bg-zinc-950/60 text-[11.5px] px-2 py-0.5 text-zinc-300">
            <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
            {s.label}
        </span>
    );
}

export default function Photos() {
    const { user } = useAuth();
    const [media, setMedia] = useState([]);
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [deletePhoto, setDeletePhoto] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [uploads, setUploads] = useState([]); // [{id, name, progress, error}]
    const [dragging, setDragging] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [reqTitle, setReqTitle] = useState("");
    const [reqDesc, setReqDesc] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const fileInputRef = useRef(null);
    const loadVersion = useRef(0);
    const uploadingIds = useRef(new Set());
    useUnsavedChanges(Boolean(showForm && (reqTitle.trim() || reqDesc.trim())) || uploads.some((item) => !item.error));

    const load = useCallback(async () => {
        const version = ++loadVersion.current;
        setLoading(true);
        setLoadError("");
        try {
            const [m, r] = await Promise.all([api.get("/media"), api.get("/change-requests")]);
            if (version !== loadVersion.current) return;
            setMedia(m.data || []);
            setRequests(r.data || []);
        } catch (_e) {
            if (version === loadVersion.current) setLoadError("We couldn't load your photos and requests. Your saved items haven't been changed.");
        } finally {
            if (version === loadVersion.current) setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        return () => { loadVersion.current += 1; };
    }, [load]);

    const uploadOne = useCallback(
        async (file, retryId = null) => {
            const tmpId = retryId || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
            if (uploadingIds.current.has(tmpId)) return;
            uploadingIds.current.add(tmpId);
            const entry = { id: tmpId, file, name: file.name, progress: 0, error: null };
            setUploads((u) => retryId ? u.map((item) => item.id === retryId ? entry : item) : [...u, entry]);
            try {
                if (file.type && !['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) throw new Error("Choose a JPG, PNG, WEBP, or GIF image.");
                if (file.size > 10 * 1024 * 1024) throw new Error("This image is over 10MB. Choose a smaller image.");
                const form = new FormData();
                form.append("file", file);
                await api.post("/media/upload", form, {
                    headers: { "Content-Type": "multipart/form-data" },
                    onUploadProgress: (e) => {
                        const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
                        setUploads((u) =>
                            u.map((x) => (x.id === tmpId ? { ...x, progress: pct } : x))
                        );
                    },
                });
                // Remove from progress list once done
                setUploads((u) => u.filter((x) => x.id !== tmpId));
            } catch (e) {
                const message = formatApiErrorDetail(e.response?.data?.detail) || e.message;
                setUploads((u) =>
                    u.map((x) => (x.id === tmpId ? { ...x, error: message, progress: 100 } : x))
                );
                toast(`Couldn't upload ${file.name}`, { description: message, duration: 3500 });
            } finally {
                uploadingIds.current.delete(tmpId);
            }
        },
        []
    );

    const handleFiles = useCallback(
        async (files) => {
            const list = Array.from(files || []);
            if (list.length === 0) return;
            // Bound concurrent transfers so a large phone selection does not
            // saturate the connection or overwhelm the existing upload API.
            for (let index = 0; index < list.length; index += 3) {
                await Promise.all(list.slice(index, index + 3).map((file) => uploadOne(file)));
            }
            await load();
        },
        [load, uploadOne]
    );

    const onDragOver = (e) => {
        e.preventDefault();
        setDragging(true);
    };
    const onDragLeave = () => setDragging(false);
    const onDrop = async (e) => {
        e.preventDefault();
        setDragging(false);
        await handleFiles(e.dataTransfer.files);
    };

    const onDeleteMedia = async () => {
        if (!deletePhoto || deleting) return;
        setDeleting(true);
        try {
            await api.delete(`/media/${deletePhoto.id}`);
            setMedia((items) => items.filter((item) => item.id !== deletePhoto.id));
            setDeletePhoto(null);
            toast.success("Photo removed");
        } catch (_e) {
            toast("Couldn't remove photo", { duration: 2500 });
        } finally { setDeleting(false); }
    };

    const submitRequest = async () => {
        const title = reqTitle.trim();
        const description = reqDesc.trim();
        if (title.length < 2 || description.length < 2) return;
        setSubmitting(true);
        try {
            const { data } = await api.post("/change-requests", { title, description });
            setRequests((r) => [data, ...r]);
            setReqTitle("");
            setReqDesc("");
            setShowForm(false);
            toast("Request sent", {
                description: "WebJesters will review and update the status.",
                duration: 2500,
            });
        } catch (e) {
            toast("Couldn't submit", {
                description: formatApiErrorDetail(e.response?.data?.detail) || e.message,
                duration: 3500,
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            className="mx-auto w-full max-w-5xl px-5 sm:px-8 pt-12 sm:pt-16 pb-40 md:pb-16"
            data-testid="photos-page"
        >
            {/* Header */}
            <div>
                <h1
                    className="text-3xl sm:text-4xl font-semibold tracking-tighter text-zinc-50"
                    data-testid="photos-heading"
                >
                    Photos &amp; requests
                </h1>
                <p className="mt-2 text-[14.5px] text-zinc-500 max-w-xl">
                    Upload job-site photography and submit website change requests. WebJesters handles the rest.
                </p>
            </div>

            {loadError && <div role="alert" data-testid="photos-load-error" className="portal-feedback mt-6 flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm">{loadError}</p>
                <button type="button" data-testid="photos-retry-button" onClick={load} disabled={loading} className="inline-flex items-center gap-2 text-sm">
                    <RefreshCw className="h-4 w-4" /> Try again
                </button>
            </div>}

            {/* ============ Photos section ============ */}
            <section className="mt-12" data-testid="section-photos">
                <div className="flex items-end justify-between mb-4">
                    <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">Photos</p>
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        data-testid="photos-upload-button"
                        className="md:hidden inline-flex items-center gap-2 h-9 px-3 rounded-md bg-zinc-50 text-zinc-950 text-[13px] font-medium hover:bg-white transition-colors"
                    >
                        <ImagePlus className="h-4 w-4" />
                        Upload
                    </button>
                </div>

                {/* Desktop drop zone */}
                <div
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop}
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="photos-dropzone"
                    role="button"
                    tabIndex={0}
                    aria-label="Upload photos"
                    onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); fileInputRef.current?.click(); } }}
                    className={[
                        "hidden md:flex cursor-pointer rounded-xl border border-dashed flex-col items-center justify-center py-10 transition-colors",
                        dragging
                            ? "border-zinc-500 bg-zinc-900/40"
                            : "border-zinc-800 hover:border-zinc-700 hover:bg-zinc-950/40",
                    ].join(" ")}
                >
                    <UploadCloud className="h-5 w-5 text-zinc-500 mb-3" />
                    <p className="text-[14px] text-zinc-200">Drop images here, or click to select</p>
                    <p className="mt-1 text-[12px] text-zinc-500">JPG, PNG, WEBP, or GIF · up to 10MB</p>
                </div>

                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    multiple
                    className="hidden"
                    onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
                    data-testid="photos-file-input"
                />

                {/* Upload progress list */}
                <AnimatePresence>
                    {uploads.length > 0 && (
                        <motion.ul
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="mt-4 space-y-2"
                            data-testid="upload-progress-list"
                        >
                            {uploads.map((u) => (
                                <li
                                    key={u.id}
                                    className="rounded-md border border-zinc-800 bg-zinc-950/40 px-3 py-2"
                                >
                                    <div className="flex items-center justify-between gap-3 text-[12.5px]">
                                        <span className="text-zinc-200 truncate">{u.name}</span>
                                        <span className="text-zinc-500 shrink-0 tabular-nums">
                                            {u.error ? "failed" : `${u.progress}%`}
                                        </span>
                                    </div>
                                    <div className="mt-2 h-0.5 bg-zinc-800 rounded-full overflow-hidden">
                                        <div
                                            className={[
                                                "h-full transition-all",
                                                u.error ? "bg-red-500/60" : "bg-zinc-300",
                                            ].join(" ")}
                                            style={{ width: `${u.progress}%` }}
                                        />
                                    </div>
                                    {u.error && <div className="mt-3 space-y-2" data-testid={`upload-error-${u.id}`} role="alert">
                                        <p className="text-xs text-red-300 break-words">{u.error}</p>
                                        <div className="flex gap-3">
                                            <button type="button" data-testid={`upload-retry-${u.id}`} className="inline-flex items-center gap-2 text-sm text-zinc-100"
                                                onClick={async () => { await uploadOne(u.file, u.id); await load(); }}><RefreshCw className="h-4 w-4" /> Retry</button>
                                            <button type="button" data-testid={`upload-dismiss-${u.id}`} className="text-sm text-zinc-400"
                                                onClick={() => setUploads((items) => items.filter((item) => item.id !== u.id))}>Dismiss</button>
                                        </div>
                                    </div>}
                                </li>
                            ))}
                        </motion.ul>
                    )}
                </AnimatePresence>

                {/* Gallery */}
                <div className="mt-6" data-testid="photos-gallery">
                    {loading ? (
                        <p className="text-[13.5px] text-zinc-500 py-6">Loading…</p>
                    ) : loadError && media.length === 0 ? null : media.length === 0 ? (
                        <p
                            className="rounded-xl border border-zinc-800 px-6 py-8 text-center text-[13.5px] text-zinc-500"
                            data-testid="photos-empty"
                        >
                            No photos yet. Your first upload will land here.
                        </p>
                    ) : (
                        <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                            {media.map((m) => (
                                <li
                                    key={m.id}
                                    className="group relative aspect-square rounded-lg overflow-hidden border border-zinc-800 bg-zinc-950"
                                    data-testid={`photo-${m.id}`}
                                >
                                    <img
                                        src={`${API}/media/${m.id}/file`}
                                        alt={m.original_filename}
                                        loading="lazy"
                                        className="h-full w-full object-cover"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setDeletePhoto(m)}
                                        data-testid={`photo-delete-${m.id}`}
                                        aria-label="Remove photo"
                                        className="absolute top-1.5 right-1.5 h-11 w-11 md:h-8 md:w-8 rounded-md bg-black/75 backdrop-blur-sm text-zinc-200 hover:bg-black/90 hover:text-red-300 transition-colors inline-flex items-center justify-center opacity-100 md:opacity-0 group-hover:opacity-100 focus:opacity-100"
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </section>

            {/* ============ Change Requests section ============ */}
            <section className="mt-16" data-testid="section-requests">
                <div className="flex items-end justify-between mb-4">
                    <div>
                        <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">
                            Change requests
                        </p>
                        <p className="mt-1.5 text-[13.5px] text-zinc-500">
                            Ask WebJesters to update copy, swap photos, or fix anything on your site.
                        </p>
                    </div>
                    {!showForm && (
                        <button
                            type="button"
                            onClick={() => setShowForm(true)}
                            data-testid="requests-new-button"
                            className="shrink-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-zinc-700 bg-zinc-900 text-zinc-100 text-[13px] font-medium hover:bg-zinc-800 transition-colors"
                        >
                            <Plus className="h-4 w-4" />
                            New
                        </button>
                    )}
                </div>

                <AnimatePresence>
                    {showForm && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                        >
                            <div
                                className="rounded-xl border border-zinc-800 p-5 mb-4 space-y-4"
                                data-testid="requests-form"
                            >
                                <div className="flex items-center justify-between">
                                    <div className="inline-flex items-center gap-2 text-[12px] text-zinc-400">
                                        <MessageSquarePlus className="h-3.5 w-3.5" />
                                        New request
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowForm(false);
                                            setReqTitle("");
                                            setReqDesc("");
                                        }}
                                        data-testid="requests-form-close"
                                        aria-label="Close form"
                                        className="h-7 w-7 inline-flex items-center justify-center rounded-md text-zinc-500 hover:text-zinc-100 hover:bg-zinc-900 transition-colors"
                                    >
                                        <X className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                                <div className="space-y-3">
                                    <input
                                        type="text"
                                        maxLength={120}
                                        placeholder="Short title (e.g. Update homepage promo)"
                                        value={reqTitle}
                                        onChange={(e) => setReqTitle(e.target.value)}
                                        data-testid="requests-form-title"
                                        className="w-full h-11 rounded-md bg-transparent border border-zinc-800 px-3 text-[14px] text-zinc-100 placeholder-zinc-600 outline-none transition-colors focus:border-zinc-500"
                                    />
                                    <textarea
                                        rows={4}
                                        maxLength={2000}
                                        placeholder="Tell us what to change. Be specific about what you'd like updated."
                                        value={reqDesc}
                                        onChange={(e) => setReqDesc(e.target.value)}
                                        data-testid="requests-form-description"
                                        className="w-full rounded-md bg-transparent border border-zinc-800 px-3 py-2.5 text-[14px] text-zinc-100 placeholder-zinc-600 outline-none transition-colors focus:border-zinc-500 leading-relaxed"
                                    />
                                </div>
                                <div className="flex justify-end">
                                    <button
                                        type="button"
                                        onClick={submitRequest}
                                        disabled={
                                            submitting ||
                                            reqTitle.trim().length < 2 ||
                                            reqDesc.trim().length < 2
                                        }
                                        data-testid="requests-form-submit"
                                        className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-zinc-50 text-zinc-950 text-[13.5px] font-medium hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                                        Send to WebJesters
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Requests list */}
                <div data-testid="requests-list">
                    {loading ? (
                        <p className="text-[13.5px] text-zinc-500 py-6">Loading…</p>
                    ) : loadError && requests.length === 0 ? null : requests.length === 0 ? (
                        <p
                            className="rounded-xl border border-dashed border-zinc-800 px-6 py-8 text-center text-[13.5px] text-zinc-500"
                            data-testid="requests-empty"
                        >
                            No requests yet. Ask for anything site-related — we'll take care of it.
                        </p>
                    ) : (
                        <ul className="rounded-xl border border-zinc-800 divide-y divide-zinc-800/80 overflow-hidden">
                            {requests.map((r) => (
                                <li key={r.id} className="p-5" data-testid={`request-${r.id}`}>
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="min-w-0">
                                            <p className="text-[14.5px] text-zinc-50 font-medium tracking-tight">
                                                {r.title}
                                            </p>
                                            <p className="mt-1 text-[13.5px] text-zinc-400 leading-relaxed whitespace-pre-line">
                                                {r.description}
                                            </p>
                                        </div>
                                        <StatusPill status={r.status} />
                                    </div>
                                    <div className="mt-3 flex items-center gap-3 text-[12px] text-zinc-500">
                                        <span>{r.submitted_by || user?.email}</span>
                                        <span>·</span>
                                        <span>{relativeTime(r.created_at)}</span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </section>

            <ConfirmPhotoDelete photo={deletePhoto} busy={deleting} onCancel={() => setDeletePhoto(null)} onConfirm={onDeleteMedia} />
            <Toaster
                theme="dark"
                position="bottom-center"
                toastOptions={{
                    style: {
                        background: "#0a0a0a",
                        border: "1px solid #27272a",
                        color: "#f4f4f5",
                    },
                }}
            />
        </section>
    );
}
